/**
 * Sommaire latéral des pages légales : surligne la section visible au scroll.
 * Progressive enhancement : les ancres restent fonctionnelles sans JS.
 */
export function initLegalToc(): void {
  const nav = document.querySelector<HTMLElement>('[data-legal-toc]');
  if (!nav) {
    return;
  }

  const links = [...nav.querySelectorAll<HTMLAnchorElement>('a[href^="#"]')];
  if (links.length === 0) {
    return;
  }

  const sections = links
    .map((link) => {
      const id = link.getAttribute('href')?.slice(1);
      return id ? document.getElementById(id) : null;
    })
    .filter((el): el is HTMLElement => el !== null);

  if (sections.length === 0) {
    return;
  }

  const getScrollOffset = (): number => {
    const root = getComputedStyle(document.documentElement);
    const headerHeight = Number.parseFloat(root.getPropertyValue('--header-h')) || 72;
    return headerHeight + 16;
  };

  // Seule la barre latérale sticky (>= 64rem) défile : en mobile le sommaire
  // est statique en haut de page, faire défiler la fenêtre ramènerait au début.
  const scroller = nav.closest<HTMLElement>('.legal-doc__sidebar');

  const revealInSidebar = (link: HTMLElement): void => {
    if (!scroller || scroller.scrollHeight <= scroller.clientHeight) {
      return;
    }

    const box = scroller.getBoundingClientRect();
    const rect = link.getBoundingClientRect();
    if (rect.top < box.top) {
      scroller.scrollTop += rect.top - box.top;
    } else if (rect.bottom > box.bottom) {
      scroller.scrollTop += rect.bottom - box.bottom;
    }
  };

  let activeId = '';

  const setActive = (id: string): void => {
    if (id === activeId) {
      return;
    }

    activeId = id;

    links.forEach((link) => {
      const hrefId = link.getAttribute('href')?.slice(1);
      const isActive = hrefId === id;
      link.classList.toggle('is-active', isActive);

      if (isActive) {
        link.setAttribute('aria-current', 'location');
        revealInSidebar(link);
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  const update = (): void => {
    const offset = getScrollOffset();
    let currentId = sections[0].id;

    for (const section of sections) {
      if (section.getBoundingClientRect().top <= offset) {
        currentId = section.id;
      }
    }

    setActive(currentId);
  };

  update();
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
}
